/**
 * Social-proof badges on the product cards of the collection and search results grid — the
 * theme's own cards, and Boost's cards while Boost rendered that grid.
 *
 * The badge decision (priority, on/off settings, wording, add-on rule) lives in one place:
 * snippets/wd-product-social-badge.liquid. This script asks the theme for those decisions in bulk
 * through templates/collection.wd-social-badges.liquid: one small request per enabled badge tag,
 * cached in sessionStorage for 10 minutes. It never requests anything per card.
 * The product page renders the same snippet directly. Quick view is excluded in assets/mrk.css.
 */
(function () {
  'use strict';

  if (window.droolSocialBadgesLoaded) return;
  window.droolSocialBadgesLoaded = true;

  var configNode = document.getElementById('wd-product-social-badge-config');
  if (!configNode) return;

  var config;
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (error) {
    return;
  }
  if (!config.view || !Array.isArray(config.tags) || !config.tags.length) return;

  // Boost's cards, and the theme's own cards in the results grid only (<results-list> in
  // sections/main-collection.liquid and search-results.liquid). Product rows elsewhere on the page
  // never had badges and must not get them.
  var CARD_SELECTOR = '.boost-sd__product-item, .boost-sd__product-item-list-view-layout, results-list product-card';
  var BADGE_SELECTOR = '.wd-social-proof-badge--collection';
  var CACHE_KEY = 'drool_social_badges_v1:' + (config.cacheKey || '');
  var CACHE_TTL = 10 * 60 * 1000;
  var MAX_PAGES = 10;

  var badges = null;
  var scheduled = false;

  function readCache() {
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.badges && Date.now() - cached.savedAt < CACHE_TTL) return cached.badges;
    } catch (error) {
      /* storage blocked or corrupt: request instead */
    }
    return null;
  }

  function writeCache(map) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), badges: map }));
    } catch (error) {
      /* storage blocked or full: the in-memory copy still works for this page */
    }
  }

  function tagUrl(tag, page) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'collections/all/' + encodeURIComponent(tag) +
      '?view=' + encodeURIComponent(config.view) + '&page=' + page;
  }

  function fetchTag(tag, page, into) {
    return fetch(tagUrl(tag, page), { credentials: 'same-origin' })
      .then(function (response) {
        if (response.status === 404) return { badges: {} };
        if (!response.ok) throw new Error('Badge lookup failed');
        return response.json();
      })
      .then(function (data) {
        var found = (data && data.badges) || {};
        Object.keys(found).forEach(function (handle) {
          into[handle] = found[handle];
        });
        if (data && data.hasNext && page < MAX_PAGES) return fetchTag(tag, page + 1, into);
        return into;
      });
  }

  function loadBadges() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);

    var map = {};
    var failed = false;
    return Promise.all(config.tags.map(function (tag) {
      return fetchTag(tag, 1, map).catch(function () {
        failed = true;
      });
    })).then(function () {
      if (!failed) writeCache(map);
      return map;
    });
  }

  function productHandle(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';

    try {
      var match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  // Boost's badge column when the card already has a Boost badge, otherwise the image wrapper
  // (mrk.css places the badge where that column would be). On the theme's own card that wrapper
  // is .card-gallery: the badge sits top-left, clear of the theme's sale badge (top-right).
  function badgeHost(card) {
    var existingLabel = card.querySelector('.boost-sd__product-label');
    if (existingLabel && existingLabel.parentElement) return existingLabel.parentElement;

    // The theme's own card with Boost's old labels (snippets/wd-product-labels.liquid): the badge
    // goes at the end of that row, as it did in Boost's label column.
    var labelRow = card.querySelector('.wd-product-labels');
    if (labelRow) return labelRow;

    var imageHost = card.querySelector('.boost-sd__product-image-wrapper') ||
      card.querySelector('.boost-sd__product-link.boost-sd__product-image') ||
      card.querySelector('.boost-sd__product-item-grid-view-layout-image') ||
      card.querySelector('.card-gallery');
    if (imageHost) imageHost.classList.add('wd-social-proof-badge-host');
    return imageHost;
  }

  function applyBadge(card, badge) {
    var node = card.querySelector(BADGE_SELECTOR);
    if (!badge || !badge.type || !badge.text) {
      if (node) node.remove();
      return;
    }

    var host = badgeHost(card);
    if (!host) return;

    // Unchanged and still last in its row: touch nothing, so the observer does not fire again.
    if (node && node.parentElement === host && !node.nextElementSibling &&
        node.getAttribute('data-wd-badge-type') === badge.type && node.textContent === badge.text) {
      return;
    }

    node = node || document.createElement('span');
    node.className = 'wd-social-proof-badge wd-social-proof-badge--collection wd-social-proof-badge--' + badge.type;
    node.setAttribute('data-wd-product-badge', 'collection');
    node.setAttribute('data-wd-badge-type', badge.type);
    node.textContent = badge.text;
    if (node.parentElement !== host || node.nextElementSibling) host.appendChild(node);
  }

  function render() {
    scheduled = false;
    if (!badges) return;

    var cards = document.querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      var handle = productHandle(cards[i]);
      if (handle) applyBadge(cards[i], badges[handle]);
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(render);
  }

  // Watch document.body, not the product list: Boost replaces the list node on filter, sort and
  // pagination, so an observer on the list ends up on a detached element (see drool-edit-spots.js).
  function watch() {
    if (typeof MutationObserver !== 'function') return;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (!mutation.addedNodes.length && !mutation.removedNodes.length) continue;
        if (mutation.target && mutation.target.closest && mutation.target.closest('.wd-social-proof-badge')) continue;
        schedule();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    watch();
    loadBadges().then(function (map) {
      badges = map;
      schedule();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
